/**
 * Centralized field mappings for rewards API payload construction and draft loading.
 *
 * This file provides a single source of truth for mapping between:
 * - UI form fields (formData in Redux store)
 * - API payload fields (sent to backend)
 */

/**
 * Field type definitions for transformation
 * @typedef {'string' | 'number' | 'boolean' | 'numberOrNull'} FieldType
 */

/**
 * Field mapping configuration
 * @typedef {Object} FieldConfig
 * @property {string} apiField - The API field name
 * @property {FieldType} type - The type for transformation
 * @property {*} [defaultValue] - Default value when field is missing (optional)
 * @property {boolean} [uiAsString] - If true, UI value is stored as string (for number inputs)
 */

export const REWARD_FIELD_MAPPINGS = {
  // Core string field
  name: {apiField: 'name', type: 'string', defaultValue: ''},

  // Boolean fields - same name in UI and API
  allCustomers: {
    apiField: 'allCustomers',
    type: 'boolean',
    defaultValue: false
  },
  segmentExclude: {
    apiField: 'segmentExclude',
    type: 'boolean',
    defaultValue: false
  },
  displayOutOfStock: {
    apiField: 'displayOutOfStock',
    type: 'boolean',
    defaultValue: false
  },
  unlimitedInventory: {
    apiField: 'unlimitedInventory',
    type: 'boolean',
    defaultValue: false
  },
  inStock: {apiField: 'inStock', type: 'boolean', defaultValue: false},
  showOnHomePage: {
    apiField: 'showOnHomePage',
    type: 'boolean',
    defaultValue: false
  },
  couponBasedLoyaltyReward: {
    apiField: 'couponBasedLoyaltyReward',
    type: 'boolean',
    defaultValue: false
  },

  // Number fields - UI stores as string, API expects number
  redeemPoints: {
    apiField: 'redeemPoints',
    type: 'numberOrNull',
    defaultValue: null,
    uiAsString: true,
    zeroAsEmpty: true
  },
  orderQty: {
    apiField: 'orderQty',
    type: 'numberOrNull',
    defaultValue: null,
    uiAsString: true,
    zeroAsEmpty: true
  },
  limitOfProductQty: {
    apiField: 'limitOfProductQty',
    type: 'numberOrNull',
    defaultValue: null,
    uiAsString: true,
    zeroAsEmpty: true
  },
  // Note: alertDisplayCount is NOT included here - it's only for BONUS_PRODUCTS and handled separately in buildPayload

  // String fields - same type in UI and API
  maxQtyPerOrder: {
    apiField: 'maxQtyPerOrder',
    type: 'string',
    defaultValue: '',
    uiAsString: true
  },
  cardType: {apiField: 'cardType', type: 'string', defaultValue: ''},
  loyaltyUrl: {apiField: 'loyaltyUrl', type: 'string', defaultValue: ''},
  digitalCodeAmount: {
    apiField: 'digitalCodeAmount',
    type: 'string',
    defaultValue: '',
    uiAsString: true,
    zeroAsEmpty: true
  },
  digitalCodePartner: {
    apiField: 'digitalCodePartner',
    type: 'string',
    defaultValue: ''
  }
};

/**
 * Transform a value to the API format based on field type
 * @param {*} value - The value to transform
 * @param {FieldType} type - The target type
 * @param {*} defaultValue - Default value if value is null/undefined
 * @returns {*} Transformed value
 */
export const transformToApiValue = (value, type, defaultValue = null) => {
  if (value === null || value === undefined || value === '') {
    // Return default for empty values
    if (defaultValue !== null && defaultValue !== undefined) {
      return defaultValue;
    }
    // Type-specific defaults
    switch (type) {
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'string':
        return '';
      case 'numberOrNull':
        return null;
      default:
        return value;
    }
  }

  switch (type) {
    case 'number':
      return Number(value) || 0;
    case 'boolean':
      return Boolean(value);
    case 'string':
      return String(value);
    case 'numberOrNull':
      if (value === '' || value === null || value === undefined) return null;
      {
        const num = Number(value);
        return Number.isFinite(num) ? num : null;
      }
    default:
      return value;
  }
};

/**
 * Transform a value from API format to UI format
 * @param {*} value - The API value
 * @param {FieldConfig} config - The field configuration
 * @returns {*} Transformed value for UI
 */
export const transformToUiValue = (value, config) => {
  const {type, defaultValue, uiAsString, zeroAsEmpty} = config;

  if (value === null || value === undefined) {
    // For UI, return appropriate default
    if (type === 'boolean') return defaultValue ?? false;
    if (uiAsString) return '';
    return defaultValue ?? '';
  }

  if (zeroAsEmpty && (value === 0 || value === '0')) {
    return '';
  }

  // Boolean fields stay as boolean
  if (type === 'boolean') {
    return Boolean(value);
  }

  // Number fields that UI stores as string
  if (uiAsString || type === 'number') {
    return String(value);
  }

  return value;
};

/**
 * Build API payload fields from formData using REWARD_FIELD_MAPPINGS
 * @param {Object} formData - The form data from Redux store
 * @returns {Object} Partial payload with mapped fields
 */
export const buildRewardFieldsPayload = (formData = {}) => {
  const payload = {};

  Object.entries(REWARD_FIELD_MAPPINGS).forEach(([uiField, config]) => {
    const value = formData?.[uiField];
    payload[config.apiField] = transformToApiValue(
      value,
      config.type,
      config.defaultValue
    );
  });

  return payload;
};
